import { createHash } from 'node:crypto'
import type { ManagedCache } from '@gzl10/nexus-sdk'

const HIBP_API = 'https://api.pwnedpasswords.com/range/'
const TIMEOUT_MS = 3000

export interface HibpOptions {
  cache?: ManagedCache<string>
  logger?: { warn: (obj: Record<string, unknown>, msg: string) => void }
}

/**
 * Check if a password appears in the HaveIBeenPwned database.
 * Uses k-Anonymity model: only first 5 chars of SHA-1 hash are sent.
 * Returns the number of times the password was found (0 = safe).
 * On API failure: returns 0 (fail-open).
 */
export async function checkLeakedPassword(password: string, options?: HibpOptions): Promise<number> {
  const sha1 = createHash('sha1').update(password).digest('hex').toUpperCase()
  const prefix = sha1.substring(0, 5)
  const suffix = sha1.substring(5)

  try {
    let rangeData = await options?.cache?.get(prefix)

    if (!rangeData) {
      const response = await fetch(`${HIBP_API}${prefix}`, {
        signal: AbortSignal.timeout(TIMEOUT_MS),
        headers: { 'User-Agent': 'Nexus-BaaS-HIBP-Check' }
      })

      if (!response.ok) {
        options?.logger?.warn({ status: response.status }, 'HIBP API returned non-OK status')
        return 0
      }

      rangeData = await response.text()
      await options?.cache?.set(prefix, rangeData)
    }

    const match = rangeData.split('\n').find(line => line.startsWith(suffix))
    if (!match) return 0

    const [, count] = match.split(':')
    return parseInt(count?.trim() ?? '0', 10) || 0
  } catch (err) {
    options?.logger?.warn(
      { error: (err as Error).message },
      'HIBP check failed, allowing password (fail-open)'
    )
    return 0
  }
}
